/**
 * Tariff Metadata
 *
 * Comprehensive metadata for all 6 supported Polish energy tariffs.
 * Provides display names, descriptions, use cases, and distributor availability.
 *
 * @see /amplify/data/utils/tariff-calculator.ts - Backend calculation logic
 * @see /frontend/src/types/graphql.ts - TariffType enum
 */

import { TariffType } from '../types/graphql';

/**
 * Tariff Metadata Interface
 * Complete metadata for a single tariff
 */
export interface TariffMetadata {
  code: TariffType;
  name: string;
  shortDescription: string;
  bestFor: string[];
  requiresSmartMeter?: boolean;
  requiresPriceData?: boolean;      // For G14: requires RCE price data
  distributors: string[];
  badge?: string;
  detailedDescription?: string;
  timeZoneInfo?: string;
  estimatedSavings?: string;
}

/**
 * Tariff Metadata Registry
 * Metadata for supported tariffs (household G and business C series)
 * Note: Business C-tariffs will be added when UI support is complete
 */
export const TARIFF_METADATA: Partial<Record<TariffType, TariffMetadata>> = {
  [TariffType.G11]: {
    code: TariffType.G11,
    name: 'G11 - Taryfa jednostrefowa',
    shortDescription: 'Stała cena przez całą dobę',
    bestFor: [
      'Równomierne zużycie przez całą dobę',
      'Gospodarstwa bez elastyczności w użytkowaniu energii',
      'Małe mieszkania (1-2 osoby)',
      'Brak możliwości przesunięcia zużycia na godziny nocne'
    ],
    distributors: ['Tauron', 'PGE', 'Enea', 'Energa'],
    detailedDescription:
      'Taryfa G11 to najprostsza taryfa z jedną stałą stawką przez całą dobę. ' +
      'Idealnie sprawdza się w gospodarstwach domowych, gdzie zużycie energii jest ' +
      'rozłożone równomiernie w ciągu dnia i nie ma możliwości przesunięcia ' +
      'głównych odbiorów (np. pranie, zmywarka) na godziny nocne.',
    timeZoneInfo: '24h - jedna stawka przez cały dzień',
    estimatedSavings: 'Brak oszczędności (taryfa bazowa)'
  },

  [TariffType.G12]: {
    code: TariffType.G12,
    name: 'G12 - Taryfa dwustrefowa',
    shortDescription: 'Dzień (06:00-22:00) i noc (22:00-06:00)',
    bestFor: [
      'Zużycie nocne (pranie, zmywarka w nocy)',
      'Pompa ciepła z zasobnikiem ciepła',
      'Ładowanie samochodu elektrycznego w nocy',
      'Gospodarstwa 3-4 osobowe z elastycznością czasową'
    ],
    distributors: ['Tauron', 'PGE', 'Enea', 'Energa'],
    detailedDescription:
      'Taryfa G12 dzieli dobę na dwie strefy: dzienną (06:00-22:00) z wyższą stawką ' +
      'oraz nocną (22:00-06:00) z niższą stawką. Wymaga możliwości przesunięcia ' +
      'co najmniej 30-40% zużycia na godziny nocne, aby była opłacalna.',
    timeZoneInfo: 'Dzień: 06:00-22:00 (16h) | Noc: 22:00-06:00 (8h)',
    estimatedSavings: 'Do 20% przy >40% zużycia nocnego'
  },

  [TariffType.G12as]: {
    code: TariffType.G12as,
    name: 'G12as - Taryfa antysmogowa',
    shortDescription: 'Preferencyjna taryfa nocna dla ogrzewania elektrycznego',
    bestFor: [
      'Ogrzewanie elektryczne całoroczne',
      'Pompa ciepła jako główne źródło ogrzewania',
      'Wysoka moc zainstalowana (>10 kW)',
      'Intensywne nocne wykorzystanie zasobnika'
    ],
    distributors: ['Tauron'],
    badge: 'Antysmog',
    detailedDescription:
      'Taryfa G12as (antysmogowa) to specjalna taryfa dla gospodarstw z ogrzewaniem ' +
      'elektrycznym. Oferuje bardzo korzystne stawki nocne, zachęcając do gromadzenia ' +
      'ciepła w zasobnikach w nocy i wykorzystywania go w dzień. Dostępna tylko dla ' +
      'Tauron Dystrybucja.',
    timeZoneInfo: 'Dzień: 06:00-22:00 | Noc: 22:00-06:00 (całoroczna)',
    estimatedSavings: 'Do 30% przy ogrzewaniu elektrycznym'
  },

  [TariffType.G12w]: {
    code: TariffType.G12w,
    name: 'G12w - Taryfa weekendowa',
    shortDescription: 'Tańsza cena w weekendy i święta',
    bestFor: [
      'Weekendowe zużycie energii',
      'Pranie i zmywanie w soboty',
      'Elastyczny harmonogram pracy (home office)',
      'Gospodarstwa z dużą aktywnością weekendową'
    ],
    distributors: ['Tauron', 'PGE', 'Enea'],
    detailedDescription:
      'Taryfa G12w łączy zróżnicowanie dzienne (dzień/noc) w dni robocze z ' +
      'całodzienną niską stawką w weekendy i święta. Idealna dla osób pracujących ' +
      'w tygodniu i przesuwających główne zużycie energii na weekendy.',
    timeZoneInfo:
      'Dni robocze: Dzień 06:00-22:00 | Noc 22:00-06:00\n' +
      'Weekendy i święta: Całodobowa niska stawka',
    estimatedSavings: 'Do 25% przy >50% zużycia weekendowego'
  },

  [TariffType.G13]: {
    code: TariffType.G13,
    name: 'G13 - Taryfa trzystrefowa sezonowa',
    shortDescription: 'Szczyty poranne/wieczorne, poza szczytem, noc',
    bestFor: [
      'Unikanie szczytów energetycznych (7-9, 17-21)',
      'Elastyczne zużycie (automaty czasowe)',
      'Gospodarstwa z systemem zarządzania energią',
      'Prosumenci z baterią domową'
    ],
    distributors: ['Tauron'],
    detailedDescription:
      'Taryfa G13 dzieli dobę na trzy strefy: szczyt (poranny 7-9 i wieczorny 17-21), ' +
      'poza szczytem (dzień poza godzinami szczytu) oraz noc. Wymaga aktywnego ' +
      'zarządzania zużyciem, aby unikać drogich godzin szczytu.',
    timeZoneInfo:
      'Szczyt: 07:00-09:00, 17:00-21:00 (zimowy)\n' +
      'Poza szczytem: pozostałe godziny dzienne + weekendy\n' +
      'Noc: 22:00-07:00',
    estimatedSavings: 'Do 35% przy unikaniu godzin szczytowych'
  },

  [TariffType.G13s]: {
    code: TariffType.G13s,
    name: 'G13s - Taryfa dynamiczna OZE',
    shortDescription: 'Najtańsza w godzinach produkcji OZE (9-17 lato)',
    bestFor: [
      'Fotowoltaika z magazynem energii',
      'Prosument z baterią domową',
      'Ładowanie samochodu elektrycznego w dzień (praca hybrydowa)',
      'System zarządzania energią (HEMS)'
    ],
    distributors: ['Tauron'],
    badge: 'NOWOŚĆ VII 2025',
    requiresSmartMeter: true,
    detailedDescription:
      'Taryfa G13s to najbardziej zaawansowana taryfa dopasowana do wzrostu ' +
      'produkcji energii odnawialnej w Polsce. Oferuje najniższe stawki w godzinach ' +
      'produkcji słonecznej (9-17 w sezonie letnim), zachęcając do wykorzystywania ' +
      'czystej energii. Wymaga inteligentnego licznika.',
    timeZoneInfo:
      'Lato (IV-IX): Tania strefa 09:00-17:00 (produkcja solarna)\n' +
      'Zimowy: Szczyt 07:00-09:00, 17:00-21:00\n' +
      'Noc: 22:00-07:00 (całoroczna)',
    estimatedSavings: 'Do 40% przy fotowoltaice + baterii'
  },

  [TariffType.G14]: {
    code: TariffType.G14,
    name: 'G14 - Dynamiczna RCE',
    shortDescription: 'Taryfa z dynamicznymi cenami godzinowymi opartymi na rynkowej cenie energii (RCE)',
    bestFor: [
      'Elastyczne zużycie energii',
      'Automatyzacja smart home',
      'Prosumenci z magazynem energii',
      'Gospodarstwa z pompą ciepła'
    ],
    distributors: ['TAURON'],
    badge: 'NOWOŚĆ 2025',
    requiresSmartMeter: true,
    detailedDescription:
      'Taryfa G14 oferuje ceny energii zmieniające się co godzinę na podstawie ' +
      'Rynkowej Ceny Energii (RCE) z PSE. Opłaty dystrybucyjne bazują na średniej stawce ' +
      'z taryfy G11. Idealna dla użytkowników z systemami automatyzacji, którzy mogą ' +
      'przesuwać zużycie na godziny z niskimi cenami RCE.',
    timeZoneInfo: 'Ceny godzinowe 0-23, publikowane z jednodniowym wyprzedzeniem',
    estimatedSavings: 'Do 30% przy optymalizacji zużycia na podstawie prognoz RCE',
    requiresPriceData: true
  },

  [TariffType.G12n]: {
    code: TariffType.G12n,
    name: 'G12n - Taryfa nocna (PGE)',
    shortDescription: 'Tania noc 01:00-05:00 dni robocze + całe weekendy',
    bestFor: [
      'Ładowanie samochodu elektrycznego w nocy',
      'Pranie i zmywanie w godzinach 01:00-05:00',
      'Gospodarstwa z elastycznym zużyciem nocnym',
      'Wykorzystanie taniej energii weekendowej'
    ],
    distributors: ['PGE'],
    badge: 'PGE',
    detailedDescription:
      'Taryfa G12n (nocna) to specyficzna taryfa PGE z bardzo krótką strefą nocną w dni ' +
      'robocze (01:00-05:00), ale z całodobową tanią strefą w weekendy i święta. ' +
      'Idealna dla osób z elastycznym zużyciem weekendowym.',
    timeZoneInfo:
      'Dni robocze: Droga strefa 05:00-01:00 | Tania noc 01:00-05:00\n' +
      'Weekendy i święta: Całodobowa tania strefa',
    estimatedSavings: 'Do 20% przy intensywnym zużyciu weekendowym'
  },

  [TariffType.G12r]: {
    code: TariffType.G12r,
    name: 'G12r - Rozszerzona weekendowa (Energa)',
    shortDescription: 'Dłuższa noc 21:00-07:00 + całe weekendy',
    bestFor: [
      'Wieczorne i poranne zużycie energii',
      'Ładowanie samochodu elektrycznego wieczorem',
      'Pranie i zmywanie po 21:00',
      'Gospodarstwa z elastycznym harmonogramem'
    ],
    distributors: ['Energa'],
    badge: 'ENERGA',
    detailedDescription:
      'Taryfa G12r (rozszerzona weekendowa) to specyficzna taryfa Energa z dłuższą strefą ' +
      'off-peak (21:00-07:00 w dni robocze) oraz całodobową tanią strefą w weekendy. ' +
      'Bardziej elastyczna niż standardowa G12w.',
    timeZoneInfo:
      'Dni robocze: Peak 07:00-21:00 | Off-peak 21:00-07:00\n' +
      'Weekendy i święta: Całodobowa off-peak strefa',
    estimatedSavings: 'Do 25% przy wieczornym/porannym zużyciu'
  }
};

/**
 * Get Tariff Metadata
 * Retrieve metadata for a specific tariff
 *
 * @param tariffCode - Tariff code (e.g., 'G11', 'G12')
 * @returns Tariff metadata or undefined if not found
 */
export function getTariffMetadata(tariffCode: TariffType): TariffMetadata | undefined {
  return TARIFF_METADATA[tariffCode];
}

/**
 * Get All Tariff Codes
 * Get array of all supported tariff codes
 *
 * @returns Array of tariff codes
 */
export function getAllTariffCodes(): TariffType[] {
  return Object.values(TariffType);
}

/**
 * Get Tariffs by Distributor
 * Filter tariffs available from a specific distributor
 *
 * @param distributorCode - Distributor code (e.g., 'TAURON')
 * @returns Array of tariff metadata
 */
export function getTariffsByDistributor(distributorCode: string): TariffMetadata[] {
  return Object.values(TARIFF_METADATA).filter((tariff): tariff is TariffMetadata =>
    tariff !== undefined && tariff.distributors.includes(distributorCode)
  );
}

/**
 * Get Tariff Display Name
 * Get short display name for a tariff
 *
 * @param tariffCode - Tariff code
 * @returns Display name (e.g., 'G11 - Taryfa jednostrefowa') or tariff code if not found
 */
export function getTariffDisplayName(tariffCode: TariffType): string {
  return TARIFF_METADATA[tariffCode]?.name ?? tariffCode;
}

/**
 * Get Tariff Badge
 * Get badge text for a tariff (if applicable)
 *
 * @param tariffCode - Tariff code
 * @returns Badge text or undefined
 */
export function getTariffBadge(tariffCode: TariffType): string | undefined {
  return TARIFF_METADATA[tariffCode]?.badge;
}

/**
 * Requires Smart Meter
 * Check if tariff requires a smart meter
 *
 * @param tariffCode - Tariff code
 * @returns True if smart meter required
 */
export function requiresSmartMeter(tariffCode: TariffType): boolean {
  return TARIFF_METADATA[tariffCode]?.requiresSmartMeter ?? false;
}
